import ReactJoyride, { STATUS, TooltipRenderProps } from 'react-joyride';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';

interface IProps {
  steps: {
    title?: string;
    target: string;
    content: string;
    disableBeacon?: boolean;
    onReady?: () => void;
    onTooltip?: () => void;
    onComplete?: () => void;
    delay?: number;
  }[];
  onRunEnd?: () => void;
  isRun?: boolean;
  isContinuous?: boolean;
  showProgress?: boolean;
  showSkipButton?: boolean;
  scrollOffset?: number;
  disableScrolling?: boolean;
  disableScrollParentFix?: boolean;
}

export default function Joyride(props: IProps) {
  const { t } = useTranslation();
  const {
    steps,
    isRun,
    isContinuous,
    onRunEnd,
    showProgress,
    showSkipButton,
    scrollOffset,
    disableScrolling,
    disableScrollParentFix,
  } = props;
  const [run, setRun] = useState(isRun);
  const [stepIndex, setStepIndex] = useState(0);

  async function startRun() {
    if (steps[0] && steps[0].delay) {
      if (steps[0] && steps[0].onReady) {
        // @ts-ignore
        await steps[0].onReady();
      }
      setTimeout(() => {
        setRun(true);
      }, steps[0].delay);
    } else {
      setRun(true);
    }
  }

  useEffect(() => {
    if (isRun) {
      startRun();
    } else {
      setRun(false);
    }
  }, [isRun]);

  function endRun() {
    // Need to set our running state to false, so we can restart if we click start again.
    setRun(false);
    setStepIndex(0);
    if (onRunEnd) {
      onRunEnd();
    }
  }

  function nextStep() {
    if (steps[stepIndex + 1] && steps[stepIndex + 1].delay) {
      setTimeout(() => {
        setStepIndex(stepIndex + 1);
      }, steps[stepIndex + 1].delay);
    } else {
      setStepIndex(stepIndex + 1);
    }
  }

  function prevStep() {
    if (steps[stepIndex - 1].delay) {
      setTimeout(() => {
        setStepIndex(stepIndex - 1);
      }, steps[stepIndex - 1].delay);
    } else {
      setStepIndex(stepIndex - 1);
    }
  }

  function TooltipComponent({
    continuous,
    index,
    step,
    backProps,
    closeProps,
    primaryProps,
    tooltipProps,
    skipProps,
  }: TooltipRenderProps) {
    return (
      <div
        {...tooltipProps}
        style={{
          backgroundColor: '#fff',
          borderRadius: '5px',
          minWidth: '300px',
          maxWidth: '400px',
          color: '#000',
        }}
      >
        {step.title && <DialogTitle>{step.title}</DialogTitle>}
        <DialogContent>
          <Grid container justifyContent="center">
            {step.content}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={2} justifyContent="space-between">
            {skipProps && (
              <Grid item>
                <Button
                  style={{
                    color: '#000',
                  }}
                  {...skipProps}
                >
                  {t('skip')}
                </Button>
              </Grid>
            )}
            <Grid
              item
              container
              spacing={1}
              style={{
                width: 'fit-content',
                justifyContent: 'flex-end',
              }}
            >
              {index > 0 && (
                <Grid item>
                  <Button color="primary" {...backProps}>
                    {t('back')}
                  </Button>
                </Grid>
              )}
              {continuous && (
                <Grid item>
                  <Button color="primary" variant="contained" {...primaryProps}>
                    {stepIndex + 1 !== steps.length ? (
                      <>
                        {t('next')} {stepIndex + 1}/{steps.length}
                      </>
                    ) : (
                      <>
                        {t('complete')} {stepIndex + 1}/{steps.length}
                      </>
                    )}
                  </Button>
                </Grid>
              )}
              {!continuous && (
                <Grid item>
                  <Button
                    size="small"
                    color="primary"
                    variant="contained"
                    {...closeProps}
                  >
                    {t('close')}
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogActions>
      </div>
    );
  }

  return (
    <ReactJoyride
      steps={steps}
      styles={{
        options: {
          zIndex: 10000,
          arrowColor: '#fff',
        },
      }}
      callback={async (data) => {
        const { action, status, type, lifecycle } = data;
        if (
          ([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status) ||
          action === 'close'
        ) {
          endRun();
        } else {
          if (isContinuous) {
            if (action === 'next' && type === 'step:after') {
              nextStep();
            } else if (action === 'prev' && type === 'step:after') {
              prevStep();
            }
          } else {
            if (type === 'step:after') {
              nextStep();
            }
          }
        }

        // step Event handler
        if (lifecycle === 'tooltip' && steps[stepIndex].onTooltip) {
          // @ts-ignore
          await steps[stepIndex].onTooltip();
        } else if (lifecycle === 'complete') {
          if (steps[stepIndex].onComplete) {
            // @ts-ignore
            await steps[stepIndex].onComplete();
          }

          // Call step onReady function on current step complete
          if (isContinuous) {
            if (action === 'next' && type === 'step:after') {
              if (steps[stepIndex + 1] && steps[stepIndex + 1].onReady) {
                // @ts-ignore
                await steps[stepIndex + 1].onReady();
              }
            } else if (action === 'prev' && type === 'step:after') {
              if (steps[stepIndex - 1] && steps[stepIndex - 1].onReady) {
                // @ts-ignore
                await steps[stepIndex - 1].onReady();
              }
            }
          } else {
            if (steps[stepIndex + 1] && steps[stepIndex + 1].onReady) {
              // @ts-ignore
              await steps[stepIndex + 1].onReady();
            }
          }
        }
      }}
      run={run}
      continuous={isContinuous}
      stepIndex={stepIndex}
      showProgress={showProgress}
      showSkipButton={showSkipButton}
      locale={{
        back: t('back'),
        close: t('close'),
        last: t('complete'),
        next: t('next'),
        open: t('open'),
        skip: t('skip'),
      }}
      disableScrolling={disableScrolling}
      disableScrollParentFix={disableScrollParentFix}
      tooltipComponent={
        TooltipComponent as React.ElementType<TooltipRenderProps>
      }
      scrollOffset={scrollOffset}
    />
  );
}
